/**
 * <s-popup-trigger> — opens a native <dialog> once per visitor after a delay.
 *
 * Attributes:
 *   target       — id of the <dialog> to open (required)
 *   delay        — milliseconds before opening. Default: 5000
 *   storage-key  — localStorage key remembering a dismissal or signup. Default: 'popup-dismissed'
 *   force-open   — open immediately (e.g. to show signup feedback after the form posts)
 *   suppress     — never auto-open (used in the theme editor)
 */
class SPopupTrigger extends HTMLElement {
  #timer = null;
  #ac = null;

  connectedCallback() {
    const dialog = document.getElementById(this.getAttribute('target') || '');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const key = this.getAttribute('storage-key') || 'popup-dismissed';
    this.#ac = new AbortController();

    dialog.addEventListener(
      'close',
      () => {
        try {
          localStorage.setItem(key, '1');
        } catch {}
      },
      { signal: this.#ac.signal }
    );

    if (this.hasAttribute('force-open')) {
      if (!dialog.open) dialog.showModal();
      return;
    }
    if (this.hasAttribute('suppress')) return;

    let dismissed = false;
    try {
      dismissed = localStorage.getItem(key) === '1';
    } catch {}
    if (dismissed) return;

    const delay = Number(this.getAttribute('delay') || 5000);
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog[open]')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }
}

if (!customElements.get('s-popup-trigger')) customElements.define('s-popup-trigger', SPopupTrigger);
